import { auth, db } from "@/lib/firebase";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { ApprovedUser, UserFormData } from "@/types";
import { toast } from "sonner";
import { normalizePhoneNumber } from "@/lib/constants";
import { logger } from "@/lib/logger";

interface DirectoryAccessContext {
  userId?: string;
  isPrivileged: boolean;
}

async function getDirectoryAccessContext(): Promise<DirectoryAccessContext> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return { isPrivileged: false };
  }

  const tokenResult = await currentUser.getIdTokenResult();
  const userId = tokenResult.claims.dsaUserId as string | undefined;
  const role = tokenResult.claims.role as string | undefined;
  const roles = Array.isArray(tokenResult.claims.roles)
    ? (tokenResult.claims.roles as string[])
    : [];
  const allRoles = [...(role ? [role] : []), ...roles];
  const isPrivileged = allRoles.some((r) =>
    ["admin", "super_admin", "business_head", "operations_user"].includes(r),
  );

  return { userId, isPrivileged };
}

/**
 * Get all users (active and inactive)
 */
export async function getAllUsers(): Promise<ApprovedUser[]> {
  try {
    const usersRef = collection(db, "approvedUsers");
    // Remove orderBy to avoid needing an index - sort in memory instead
    const querySnapshot = await getDocs(usersRef);

    const users: ApprovedUser[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      users.push(mapUserData(doc.id, data));
    });

    // Sort by name in memory
    users.sort((a, b) => a.name.localeCompare(b.name));

    return users;
  } catch (error) {
    logger.error("Failed to fetch all users", error);
    toast.error("Failed to fetch users");
    return [];
  }
}

/**
 * Get all active approved users
 */
export async function getAllActiveUsers(): Promise<ApprovedUser[]> {
  try {
    const accessContext = await getDirectoryAccessContext();
    const usersRef = collection(db, "approvedUsers");

    const usersMap = new Map<string, ApprovedUser>();

    if (accessContext.isPrivileged) {
      // Privileged users can list all active users.
      const q = query(usersRef, where("isActive", "==", true));
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        usersMap.set(docSnap.id, mapUserData(docSnap.id, data));
      });
    } else if (accessContext.userId) {
      // Least-privilege directory scope: self + direct reports.
      const selfDoc = await getDoc(
        doc(db, "approvedUsers", accessContext.userId),
      );
      if (selfDoc.exists()) {
        const selfData = selfDoc.data();
        if (selfData.isActive === true) {
          usersMap.set(selfDoc.id, mapUserData(selfDoc.id, selfData));
        }
      }

      const managedQuery = query(
        usersRef,
        where("reportingTo", "==", accessContext.userId),
        where("isActive", "==", true),
      );
      const managedSnapshot = await getDocs(managedQuery);
      managedSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        usersMap.set(docSnap.id, mapUserData(docSnap.id, data));
      });
    }

    const users: ApprovedUser[] = Array.from(usersMap.values());

    // Sort by name in memory
    users.sort((a, b) => a.name.localeCompare(b.name));

    return users;
  } catch (error) {
    logger.error("Failed to fetch active users", error);
    toast.error("Failed to fetch active users");
    return [];
  }
}

/**
 * Get users by office ID
 */
export async function getUsersByOffice(
  officeId: string,
): Promise<ApprovedUser[]> {
  try {
    const usersRef = collection(db, "approvedUsers");
    const q = query(
      usersRef,
      where("officeId", "==", officeId),
      where("isActive", "==", true),
      orderBy("name", "asc"),
    );
    const querySnapshot = await getDocs(q);

    const users: ApprovedUser[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      users.push(mapUserData(doc.id, data));
    });

    return users;
  } catch (error) {
    logger.error("Failed to fetch users by office", error, { officeId });
    toast.error("Failed to fetch users by office");
    return [];
  }
}

/**
 * Get users by role
 */
export async function getUsersByRole(role: string): Promise<ApprovedUser[]> {
  try {
    const usersRef = collection(db, "approvedUsers");
    const q = query(
      usersRef,
      where("roles", "array-contains", role),
      where("isActive", "==", true),
      orderBy("name", "asc"),
    );
    const querySnapshot = await getDocs(q);

    const users: ApprovedUser[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      users.push(mapUserData(doc.id, data));
    });

    return users;
  } catch (error) {
    logger.error("Failed to fetch users by role", error, { role });
    toast.error("Failed to fetch users by role");
    return [];
  }
}

/**
 * Get user by ID
 */
export async function getUserById(
  userId: string,
): Promise<ApprovedUser | null> {
  try {
    const userDoc = await getDoc(doc(db, "approvedUsers", userId));

    if (!userDoc.exists()) {
      return null;
    }

    return mapUserData(userDoc.id, userDoc.data());
  } catch (error) {
    logger.error("Failed to fetch user by ID", error, { userId });
    toast.error("Failed to fetch user");
    return null;
  }
}

/**
 * Create a new user
 */
export async function createUser(formData: UserFormData): Promise<string> {
  try {
    // Validate required fields
    if (!formData.name || !formData.phone || !formData.officeId) {
      throw new Error("Name, phone number, and office are required");
    }

    if (!formData.roles || formData.roles.length === 0) {
      throw new Error("At least one role must be selected");
    }

    if (!formData.primaryRole) {
      throw new Error("Primary role must be selected");
    }

    // Check if user with same phone number already exists
    const usersRef = collection(db, "approvedUsers");
    const existingUserQuery = query(
      usersRef,
      where("phone", "==", normalizePhoneNumber(formData.phone)),
    );
    const existingUserSnapshot = await getDocs(existingUserQuery);

    if (!existingUserSnapshot.empty) {
      throw new Error("User with this phone number already exists");
    }

    // Prepare user data
    const userData: any = {
      name: formData.name,
      email: formData.email || "",
      phone: normalizePhoneNumber(formData.phone),
      userId: "", // Placeholder; will be replaced with the auto-generated doc ID below
      officeId: formData.officeId,
      roles: formData.roles,
      primaryRole: formData.primaryRole,
      isActive: true,
      createdAt: serverTimestamp(),
      fcmTokens: [],
    };

    // Add optional fields
    if (formData.employeeId) userData.employeeId = formData.employeeId;
    if (formData.designation) userData.designation = formData.designation;
    if (formData.department) userData.department = formData.department;
    if (formData.joiningDate)
      userData.joiningDate = new Date(formData.joiningDate);
    if (formData.reportingTo && formData.reportingTo.trim())
      userData.reportingTo = formData.reportingTo;

    // Add emergency contact if provided
    if (formData.emergencyContact) {
      userData.emergencyContact = formData.emergencyContact;
    }

    // Pre-generate the document ref so we can embed its ID as the userId field.
    // This ensures userId field == document ID == dsaUserId custom claim == user.id in the app.
    const docRef = doc(usersRef);
    userData.userId = docRef.id;
    await setDoc(docRef, userData);
    logger.info("User created successfully", {
      userId: docRef.id,
      phone: formData.phone,
      role: formData.primaryRole,
    });
    toast.success("User created successfully!");
    return docRef.id;
  } catch (error) {
    logger.error("Failed to create user", error, {
      phone: formData.phone,
      name: formData.name,
    });
    if (error instanceof Error) {
      toast.error(error.message);
    } else {
      toast.error("Failed to create user");
    }
    throw error;
  }
}

/**
 * Update an existing user
 */
export async function updateUser(
  userId: string,
  formData: Partial<UserFormData>,
): Promise<void> {
  try {
    const userRef = doc(db, "approvedUsers", userId);

    // Prepare update data
    const updateData: any = {
      updatedAt: serverTimestamp(),
    };

    // Add fields that are present in formData
    if (formData.name !== undefined) updateData.name = formData.name;
    if (formData.email !== undefined) updateData.email = formData.email;
    if (formData.phone !== undefined) {
      updateData.phone = normalizePhoneNumber(formData.phone);
    }
    if (formData.officeId !== undefined)
      updateData.officeId = formData.officeId;
    if (formData.roles !== undefined) updateData.roles = formData.roles;
    if (formData.primaryRole !== undefined)
      updateData.primaryRole = formData.primaryRole;
    if (formData.employeeId !== undefined)
      updateData.employeeId = formData.employeeId;
    if (formData.designation !== undefined)
      updateData.designation = formData.designation;
    if (formData.department !== undefined)
      updateData.department = formData.department;
    if (formData.joiningDate !== undefined && formData.joiningDate)
      updateData.joiningDate = new Date(formData.joiningDate);
    if (formData.reportingTo !== undefined && formData.reportingTo.trim())
      updateData.reportingTo = formData.reportingTo;

    // Update emergency contact if provided
    if (formData.emergencyContact) {
      updateData.emergencyContact = formData.emergencyContact;
    }

    await updateDoc(userRef, updateData);
    toast.success("User updated successfully!");
  } catch (error) {
    logger.error("Failed to update user", error, { userId });
    toast.error("Failed to update user");
    throw error;
  }
}

/**
 * Deactivate a user (soft delete)
 */
export async function deactivateUser(userId: string): Promise<void> {
  try {
    const userRef = doc(db, "approvedUsers", userId);
    await updateDoc(userRef, {
      isActive: false,
      deactivatedAt: serverTimestamp(),
    });
    logger.info("User deactivated successfully", { userId });
    toast.success("User deactivated successfully");
  } catch (error) {
    logger.error("Failed to deactivate user", error, { userId });
    toast.error("Failed to deactivate user");
    throw error;
  }
}

/**
 * Activate a user
 */
export async function activateUser(userId: string): Promise<void> {
  try {
    const userRef = doc(db, "approvedUsers", userId);
    await updateDoc(userRef, {
      isActive: true,
      reactivatedAt: serverTimestamp(),
    });
    logger.info("User activated successfully", { userId });
    toast.success("User activated successfully");
  } catch (error) {
    logger.error("Failed to activate user", error, { userId });
    toast.error("Failed to activate user");
    throw error;
  }
}

/**
 * Helper function to map Firestore data to ApprovedUser
 */
function mapUserData(id: string, data: any): ApprovedUser {
  return {
    id: id, // Document ID
    phone: data.phone || data.phoneNumber, // Support migration from phoneNumber
    userId: data.userId || id,
    name: data.name,
    email: data.email || "",
    roles: data.roles || [],
    primaryRole: data.primaryRole,
    officeId: data.officeId || "",
    isActive: data.isActive ?? true,
    teamId: data.teamId,
    employeeId: data.employeeId,
    designation: data.designation,
    department: data.department,
    joiningDate:
      data.joiningDate instanceof Timestamp
        ? data.joiningDate.toDate()
        : typeof data.joiningDate === "string"
          ? data.joiningDate
          : data.joiningDate instanceof Date
            ? data.joiningDate
            : undefined,
    reportingTo: data.reportingTo,
    emergencyContact: data.emergencyContact,
    lastTokenUpdate: data.lastTokenUpdate,
    fcmTokens: data.fcmTokens || [],
    createdAt:
      data.createdAt instanceof Timestamp
        ? data.createdAt.toDate()
        : data.createdAt instanceof Date
          ? data.createdAt
          : undefined,
    updatedAt:
      data.updatedAt instanceof Timestamp
        ? data.updatedAt.toDate()
        : data.updatedAt instanceof Date
          ? data.updatedAt
          : undefined,
    deactivatedAt:
      data.deactivatedAt instanceof Timestamp
        ? data.deactivatedAt.toDate()
        : data.deactivatedAt instanceof Date
          ? data.deactivatedAt
          : undefined,
    reactivatedAt:
      data.reactivatedAt instanceof Timestamp
        ? data.reactivatedAt.toDate()
        : data.reactivatedAt instanceof Date
          ? data.reactivatedAt
          : undefined,
  };
}
